import React from 'react';
import { Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    targetAmount: Yup.number()
        .required('Target amount is required')
        .positive('Amount must be positive'),
    startDate: Yup.date()
        .required('Start date is required'),
    targetDate: Yup.date()
        .required('Target date is required')
        .min(
            Yup.ref('startDate'),
            'Target date must be after start date'
        ),
    description: Yup.string(),
    priority: Yup.string().required('Priority is required'),
    category: Yup.string().required('Category is required'),
    notifyEmail: Yup.boolean(),
    notifyThreshold: Yup.number()
        .when('notifyEmail', {
            is: true,
            then: () => Yup.number()
                .required('Notification threshold is required')
                .min(1, 'Must be at least 1%')
                .max(100, 'Must be less than 100%')
        })
});

const PRIORITIES = ['low', 'medium', 'high'];
const CATEGORIES = ['savings', 'investment', 'debt', 'purchase', 'education', 'retirement', 'other'];

export const GoalForm = ({ onSubmit, onCancel, initialValues = null }) => {
    const defaultValues = {
        name: '',
        targetAmount: '',
        currentAmount: 0,
        startDate: '',
        targetDate: '',
        description: '',
        priority: 'medium',
        category: 'savings',
        notifyEmail: false,
        notifyThreshold: 80,
        ...initialValues
    };

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Goal Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            isInvalid={touched.name && errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Target Amount</Form.Label>
                        <Form.Control
                            type="number"
                            name="targetAmount"
                            value={values.targetAmount}
                            onChange={handleChange}
                            isInvalid={touched.targetAmount && errors.targetAmount}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.targetAmount}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {initialValues && (
                        <Form.Group className="mb-3">
                            <Form.Label>Current Amount</Form.Label>
                            <Form.Control
                                type="number"
                                name="currentAmount"
                                value={values.currentAmount}
                                onChange={handleChange}
                                isInvalid={touched.currentAmount && errors.currentAmount}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.currentAmount}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="startDate"
                            value={values.startDate}
                            onChange={handleChange}
                            isInvalid={touched.startDate && errors.startDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.startDate}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Target Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="targetDate"
                            value={values.targetDate}
                            onChange={handleChange}
                            isInvalid={touched.targetDate && errors.targetDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.targetDate}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                            name="category"
                            value={values.category}
                            onChange={handleChange}
                            isInvalid={touched.category && errors.category}
                        >
                            {CATEGORIES.map((category) => (
                                <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.category}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Priority</Form.Label>
                        <Form.Select
                            name="priority"
                            value={values.priority}
                            onChange={handleChange}
                            isInvalid={touched.priority && errors.priority}
                        >
                            {PRIORITIES.map((priority) => (
                                <option key={priority} value={priority}>
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.priority}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            rows={3}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="notifyEmail"
                            label="Email Notifications"
                            checked={values.notifyEmail}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    {values.notifyEmail && (
                        <Form.Group className="mb-3">
                            <Form.Label>Notification Threshold (%)</Form.Label>
                            <Form.Control
                                type="number"
                                name="notifyThreshold"
                                value={values.notifyThreshold}
                                onChange={handleChange}
                                isInvalid={touched.notifyThreshold && errors.notifyThreshold}
                                min="1"
                                max="100"
                            />
                            <Form.Text className="text-muted">
                                You'll receive an email when you reach this percentage of your goal
                            </Form.Text>
                            <Form.Control.Feedback type="invalid">
                                {errors.notifyThreshold}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save Goal'}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}; 